import { useEffect, useMemo, useState } from 'react'
import { Heart, Map, Search, SlidersHorizontal, X } from 'lucide-react'
import ShopCard from './components/ShopCard'
import ShopDetail from './components/ShopDetail'
import ShopMap from './components/ShopMap'
import { categories, regions, shops } from './data/shops'

const FAVORITES_KEY = 'old-busan-vintage-favorites'

function readFavorites() {
  if (typeof window === 'undefined') return []

  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || []
  } catch {
    return []
  }
}

export default function App() {
  const [query, setQuery] = useState('')
  const [region, setRegion] = useState('전체 지역')
  const [category, setCategory] = useState('전체 카테고리')
  const [favorites, setFavorites] = useState(readFavorites)
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [selectedShop, setSelectedShop] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites))
  }, [favorites])

  const filteredShops = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('ko')

    return shops
      .filter((shop) => !normalizedQuery || shop.name.toLocaleLowerCase('ko').includes(normalizedQuery))
      .filter((shop) => region === '전체 지역' || shop.region === region)
      .filter((shop) => category === '전체 카테고리' || shop.category === category)
      .filter((shop) => !favoritesOnly || favorites.includes(shop.id))
      .map((shop) => ({ ...shop, index: shops.findIndex((item) => item.id === shop.id) }))
  }, [query, region, category, favoritesOnly, favorites])

  const activeShop =
    selectedShop && filteredShops.some((shop) => shop.id === selectedShop.id)
      ? selectedShop
      : null

  const toggleFavorite = (shopId) => {
    setFavorites((current) =>
      current.includes(shopId) ? current.filter((id) => id !== shopId) : [...current, shopId],
    )
  }

  const resetFilters = () => {
    setQuery('')
    setRegion('전체 지역')
    setCategory('전체 카테고리')
    setFavoritesOnly(false)
  }

  const activeFilterCount = [region !== '전체 지역', category !== '전체 카테고리', favoritesOnly].filter(Boolean).length

  return (
    <main className="app-shell">
      <header className="masthead">
        <a className="brand" href="#" aria-label="오래된 부산 홈">
          <span className="brand__issue">ISSUE 01 · 2026</span>
          <span className="brand__name">오래된 부산</span>
          <span className="brand__sub">VINTAGE MAP & DIRECTORY</span>
        </a>
        <div className="masthead__note">
          <span>35° 10′ N</span>
          <span>129° 04′ E</span>
        </div>
        <p>공개 정보로 확인한<br />부산의 빈티지 공간 {shops.length}곳</p>
      </header>

      <section className="toolbar" aria-label="매장 검색 및 필터">
        <label className="search-field">
          <Search size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="매장 이름으로 찾기"
            aria-label="매장 이름 검색"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="검색어 지우기">
              <X size={16} />
            </button>
          )}
        </label>

        <div className={`filter-group ${filtersOpen ? 'is-open' : ''}`}>
          <select value={region} onChange={(event) => setRegion(event.target.value)} aria-label="지역 필터">
            <option>전체 지역</option>
            {regions.map((item) => <option key={item}>{item}</option>)}
          </select>
          <select value={category} onChange={(event) => setCategory(event.target.value)} aria-label="카테고리 필터">
            <option>전체 카테고리</option>
            {categories.map((item) => <option key={item}>{item}</option>)}
          </select>
          <button
            type="button"
            className={`favorites-filter ${favoritesOnly ? 'is-active' : ''}`}
            onClick={() => setFavoritesOnly((value) => !value)}
          >
            <Heart size={16} fill={favoritesOnly ? 'currentColor' : 'none'} />
            저장한 곳
          </button>
        </div>

        <button
          className="mobile-filter-button"
          type="button"
          onClick={() => setFiltersOpen((value) => !value)}
          aria-expanded={filtersOpen}
        >
          <SlidersHorizontal size={17} />
          필터
          {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
        </button>
      </section>

      <section className="directory">
        <aside className="list-panel" aria-label="빈티지 숍 목록">
          <div className="list-heading">
            <p><span>{String(filteredShops.length).padStart(2, '0')}</span> SHOPS FOUND</p>
            {(activeFilterCount > 0 || query) && (
              <button type="button" onClick={resetFilters}>모두 지우기</button>
            )}
          </div>
          <div className="shop-list">
            {filteredShops.length ? (
              filteredShops.map((shop) => (
                <ShopCard
                  key={shop.id}
                  shop={shop}
                  selected={activeShop?.id === shop.id}
                  favorite={favorites.includes(shop.id)}
                  onFavorite={toggleFavorite}
                  onSelect={setSelectedShop}
                />
              ))
            ) : (
              <div className="empty-state">
                <span>NO MATCH</span>
                <h2>조건에 맞는 가게가 없어요.</h2>
                <p>필터를 조금 넓혀 다른 골목도 둘러보세요.</p>
                <button type="button" onClick={resetFilters}>전체 매장 보기</button>
              </div>
            )}
          </div>
        </aside>

        <div className="map-panel">
          <div className="map-label">
            <Map size={15} />
            BUSAN, SOUTH KOREA
          </div>
          <ShopMap
            shops={filteredShops}
            selectedShop={activeShop}
            favorites={favorites}
            onSelect={setSelectedShop}
          />
          <div className="map-legend">
            <span><i /> VINTAGE SHOP</span>
            <span><Heart size={12} fill="currentColor" /> SAVED</span>
          </div>
          <ShopDetail
            shop={activeShop}
            favorite={activeShop ? favorites.includes(activeShop.id) : false}
            onFavorite={toggleFavorite}
            onClose={() => setSelectedShop(null)}
          />
        </div>
      </section>

      <footer>
        <span>OLD BUSAN ARCHIVE</span>
        <p>2026.07 공개 정보 기준 · 방문 전 공식 채널을 확인하세요.</p>
        <span>MAP DATA © OPENSTREETMAP</span>
      </footer>
    </main>
  )
}
